import { useEffect, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import type { KnowledgeBase, LabContent } from './types';
import { LabConfiguratorShell } from './LabConfiguratorShell';

type ProductPageGenerateMode = 'content' | 'combo' | 'gallery';

interface ProductPageGenerateModalProps {
  knowledgeBases: KnowledgeBase[];
  contents?: LabContent[];
  onClose: () => void;
  onSubmit: (data: {
    mode: ProductPageGenerateMode;
    knowledgeBaseId: string;
    labContentId?: string;
  }) => Promise<void> | void;
}

export function ProductPageGenerateModal({
  knowledgeBases,
  contents = [],
  onClose,
  onSubmit,
}: ProductPageGenerateModalProps) {
  const completedKnowledgeBases = useMemo(
    () => knowledgeBases.filter(kb => kb.isComplete),
    [knowledgeBases],
  );
  const [knowledgeBaseId, setKnowledgeBaseId] = useState(completedKnowledgeBases[0]?.id || '');
  const [mode, setMode] = useState<ProductPageGenerateMode>('combo');
  const [labContentId, setLabContentId] = useState('');
  const availableContents = contents.filter(content => content.type === 'LANDING' && content.status === 'COMPLETE' && content.knowledgeBaseId === knowledgeBaseId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setKnowledgeBaseId(prev => prev || completedKnowledgeBases[0]?.id || '');
  }, [completedKnowledgeBases]);


  const handleSubmit = async () => {
    if (!knowledgeBaseId) {
      setError('Wybierz bazę wiedzy.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        mode,
        knowledgeBaseId,
        ...(mode === 'gallery' && availableContents.some(content => content.id === labContentId) ? { labContentId } : {}),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nie udało się rozpocząć generowania.';
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <LabConfiguratorShell
      title="Generuj stronę produktową"
      description="Wybierz zakres generowania. Własne grafiki możesz wgrać z komputera do pól zapisanej strony."
      icon={<FileText className="w-12 h-12" />}
      onClose={onClose}
      closeDisabled={submitting}
      maxWidthClassName="max-w-3xl"
      footer={(
        <div className="flex items-center justify-center gap-4">
          <Button
            onClick={handleSubmit}
            loading={submitting}
            disabled={!knowledgeBaseId}
          >
            Generuj
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Anuluj
          </Button>
        </div>
      )}
    >
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-text-primary mb-2">Co generujemy?</legend>
        {([
          ['combo', 'Teksty + Grafiki', 'Najpierw powstaną teksty, następnie automatycznie grafiki do strony.'],
          ['content', 'Same teksty', 'Pola na grafiki pozostaną puste, gotowe na pliki z komputera.'],
          ['gallery', 'Same grafiki', 'Grafiki na podstawie bazy wiedzy lub istniejących tekstów strony.'],
        ] as const).map(([value, label, description]) => (
          <label key={value} className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer ${mode === value ? 'border-accent-primary bg-accent-primary/10' : 'border-border-subtle bg-surface-hover/30'}`}>
            <input type="radio" name="product-page-mode" value={value} checked={mode === value} onChange={() => setMode(value)} className="mt-1 accent-accent-primary" />
            <span><span className="block text-sm font-medium text-text-primary">{label}</span><span className="block text-xs text-text-muted mt-1">{description}</span></span>
          </label>
        ))}
      </fieldset>

      <label className="space-y-1.5 block">
        <span className="text-sm text-text-secondary">Baza wiedzy</span>
        <Select
          value={knowledgeBaseId}
          onChange={event => setKnowledgeBaseId(event.target.value)}
          className="input w-full text-sm"
        >
          {completedKnowledgeBases.map(kb => (
            <option key={kb.id} value={kb.id}>{kb.name}</option>
          ))}
        </Select>
      </label>
      {mode === 'gallery' && availableContents.length > 0 && (
        <label className="space-y-1.5 block">
          <span className="text-sm text-text-secondary">Wersja strony dla grafik</span>
          <Select value={availableContents.some(content => content.id === labContentId) ? labContentId : ''} onChange={event => setLabContentId(event.target.value)}>
            <option value="">Nowa wersja, na podstawie bazy wiedzy</option>
            {availableContents.map(content => <option key={content.id} value={content.id}>{content.title}</option>)}
          </Select>
        </label>
      )}
      {mode !== 'content' && <p className="text-xs text-text-muted">Generowanie grafik korzysta z zapisanych zdjęć referencyjnych i stylu produktu. Wymaga połączonego Dysku Google. Istniejące grafiki w wybranej wersji zostaną zachowane.</p>}

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}
    </LabConfiguratorShell>
  );
}
